import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, AlertCircle } from "lucide-react";
import { authService } from "@/services/authService";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get token and handle various encoding formats
  // Try multiple methods to get token from URL
  let token: string | null = null;
  
  // Method 1: Try useSearchParams (react-router)
  token = searchParams.get("token");
  
  // Method 2: Fallback to window.location.search
  if (!token) {
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get("token");
  }
  
  // Method 3: Fallback to direct URL parsing (handle HTML entities)
  if (!token) {
    const url = window.location.href;
    const match = url.match(/[?&]token=([^&]+)/);
    if (match && match[1]) {
      token = match[1];
    }
  }
  
  // Decode the token - handle URL encoding and HTML entities
  if (token) {
    try {
      // Decode URL encoding
      token = decodeURIComponent(token);
      // Handle HTML entities (&#x3D; = =, &#61; = =)
      token = token.replace(/&#x3D;/g, '=').replace(/&#61;/g, '=').replace(/&amp;/g, '&');
      // Clean up any remaining encoding issues
      token = token.trim();
    } catch (e) {
      // If decoding fails, try to use as-is
      console.warn('Failed to decode token:', e);
    }
  }
  
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (!token || token.trim() === "") {
      setStatus("error");
      setErrorMessage("No verification token provided");
      return;
    }

    const verifyEmail = async () => {
      try {
        await authService.verifyEmail(token);
        setStatus("success");
      } catch (error: any) {
        setStatus("error");
        setErrorMessage(
          error.response?.data?.message || 
          error.message || 
          "Failed to verify email. The link may have expired."
        );
      }
    };

    verifyEmail();
  }, [token]);

  // Separate effect for countdown and navigation
  useEffect(() => {
    if (status !== "success") return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  // Separate effect for navigation when countdown reaches 0
  useEffect(() => {
    if (status === "success" && countdown === 0) {
      navigate("/", { replace: true });
    }
  }, [status, countdown, navigate]);

  const handleGoToLogin = () => {
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-primary/20 to-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            {status === "loading" && (
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
            )}
            {status === "success" && (
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            )}
            {status === "error" && (
              <XCircle className="h-12 w-12 text-destructive" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {status === "loading" && "Verifying Email"}
            {status === "success" && "Email Verified!"}
            {status === "error" && "Verification Failed"}
          </CardTitle>
          <CardDescription>
            {status === "loading" && "Please wait while we verify your email address..."}
            {status === "success" && "Your email has been successfully verified. You can now sign in."}
            {status === "error" && errorMessage}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "success" && (
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Redirecting to login in {countdown} seconds...
              </p>
              <Button onClick={handleGoToLogin} className="w-full">
                Go to Login
              </Button>
            </div>
          )}
          {status === "error" && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Verification Failed</AlertTitle>
                <AlertDescription>
                  {errorMessage}
                </AlertDescription>
              </Alert>
              <div className="space-y-2">
                <Button onClick={handleGoToLogin} variant="outline" className="w-full">
                  Go to Login
                </Button>
                <Button
                  onClick={() => {
                    // Resend verification - user would need to use the resend feature from login page
                    navigate("/", { replace: true });
                  }}
                  className="w-full"
                >
                  Request New Verification Email
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
